/**
 * Geometry Utilities
 *
 * Utilities for geometric calculations like point-in-polygon detection.
 *
 * @example
 * ```ts
 * import { isPointInPolygon, detectWallSection } from '@/utils/geometry';
 *
 * // Check if a point is inside a polygon
 * const inside = isPointInPolygon(
 *   { x: 0.5, y: 0.5 },
 *   [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }]
 * );
 * // Result: true
 *
 * // Detect which wall section contains a point
 * const wallSection = detectWallSection({ x: 0.5, y: 0.5 }, regions);
 * // Result: 'Main Wall' or null
 * ```
 */

import type { Point, WallSectionRegion } from '@flash/shared';

/**
 * Check if a point is inside a polygon using ray casting algorithm
 *
 * Uses the "ray casting" or "crossing number" algorithm:
 * Cast a ray from the point to the right and count edge crossings.
 * If odd number of crossings, the point is inside.
 *
 * @param point - Point to test (normalized 0-1 coordinates)
 * @param polygon - Array of polygon vertices (normalized 0-1 coordinates)
 * @returns True if point is inside the polygon
 *
 * @example
 * ```ts
 * const inside = isPointInPolygon(
 *   { x: 0.5, y: 0.5 },
 *   [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }]
 * );
 * // Result: true
 * ```
 */
export const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
  if (polygon.length < 3) {
    return false;
  }

  let inside = false;
  const { x, y } = point;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pointI = polygon[i];
    const pointJ = polygon[j];

    // Safety check for array bounds (should never happen after length check)
    if (!pointI || !pointJ) continue;

    const xi = pointI.x;
    const yi = pointI.y;
    const xj = pointJ.x;
    const yj = pointJ.y;

    // Check if the ray crosses this edge
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
};

/**
 * Detect which wall section contains a point
 *
 * Iterates through floor plan regions and returns the first one
 * that contains the point. Returns null if no region contains the point.
 *
 * @param point - Point to test (normalized 0-1 coordinates)
 * @param regions - Array of floor plan regions with polygons
 * @returns Wall section name if found, null otherwise
 *
 * @example
 * ```ts
 * const wallSection = detectWallSection({ x: 0.5, y: 0.5 }, floorPlan.regions);
 * // Result: 'Main Wall' or null
 * ```
 */
export const detectWallSection = (point: Point, regions: WallSectionRegion[]): string | null => {
  for (const region of regions) {
    if (isPointInPolygon(point, region.polygon)) {
      return region.wallSectionName;
    }
  }
  return null;
};

/**
 * Calculate the centroid of a polygon
 *
 * @param polygon - Array of polygon vertices
 * @returns Centroid point
 *
 * @example
 * ```ts
 * const center = calculatePolygonCentroid([
 *   { x: 0, y: 0 },
 *   { x: 1, y: 0 },
 *   { x: 1, y: 1 },
 *   { x: 0, y: 1 },
 * ]);
 * // Result: { x: 0.5, y: 0.5 }
 * ```
 */
export const calculatePolygonCentroid = (polygon: Point[]): Point => {
  if (polygon.length === 0) {
    return { x: 0, y: 0 };
  }

  let sumX = 0;
  let sumY = 0;

  for (const point of polygon) {
    sumX += point.x;
    sumY += point.y;
  }

  return {
    x: sumX / polygon.length,
    y: sumY / polygon.length,
  };
};

/**
 * Calculate the signed distance from a point to the nearest polygon edge
 * Positive if inside, negative if outside
 *
 * @param point - Point to test
 * @param polygon - Polygon vertices
 * @returns Signed distance (positive = inside, negative = outside)
 */
const signedDistanceToPolygon = (point: Point, polygon: Point[]): number => {
  const inside = isPointInPolygon(point, polygon);
  let minDist = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i]!;
    const p2 = polygon[(i + 1) % polygon.length]!;

    // Calculate distance to edge
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const lengthSquared = dx * dx + dy * dy;

    let dist: number;
    if (lengthSquared === 0) {
      // Segment is a point
      const ddx = point.x - p1.x;
      const ddy = point.y - p1.y;
      dist = Math.sqrt(ddx * ddx + ddy * ddy);
    } else {
      // Project point onto segment
      const t = Math.max(
        0,
        Math.min(1, ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lengthSquared)
      );
      const projX = p1.x + t * dx;
      const projY = p1.y + t * dy;
      const ddx = point.x - projX;
      const ddy = point.y - projY;
      dist = Math.sqrt(ddx * ddx + ddy * ddy);
    }

    minDist = Math.min(minDist, dist);
  }

  return inside ? minDist : -minDist;
};

/**
 * Cell class for pole of inaccessibility algorithm
 */
interface Cell {
  x: number;
  y: number;
  halfSize: number;
  distance: number;
  maxDistance: number;
}

/**
 * Create a cell for the pole of inaccessibility algorithm
 */
const createCell = (x: number, y: number, halfSize: number, polygon: Point[]): Cell => {
  const distance = signedDistanceToPolygon({ x, y }, polygon);
  return {
    x,
    y,
    halfSize,
    distance,
    // Maximum possible distance within this cell
    maxDistance: distance + halfSize * Math.SQRT2,
  };
};

/**
 * Find the visual center (pole of inaccessibility) of a polygon
 *
 * This finds the point inside a polygon that is furthest from any edge.
 * Unlike the centroid, this point is guaranteed to be inside the polygon,
 * even for concave shapes.
 *
 * Based on the polylabel algorithm by Mapbox.
 *
 * @param polygon - Array of polygon vertices
 * @param precision - Precision of the result (default: 0.01 of polygon size)
 * @returns The visual center point, guaranteed to be inside the polygon
 *
 * @example
 * ```ts
 * // For a concave polygon, centroid might be outside, but visualCenter is inside
 * const center = findVisualCenter([
 *   { x: 0, y: 0 },
 *   { x: 0.5, y: 0.3 },  // Concave point
 *   { x: 1, y: 0 },
 *   { x: 1, y: 1 },
 *   { x: 0, y: 1 },
 * ]);
 * ```
 */
export const findVisualCenter = (polygon: Point[], precision?: number): Point => {
  if (polygon.length < 3) {
    return polygon[0] ?? { x: 0, y: 0 };
  }

  // Find bounding box
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const p of polygon) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  const width = maxX - minX;
  const height = maxY - minY;
  const cellSize = Math.min(width, height);

  // Set precision based on polygon size if not provided
  const actualPrecision = precision ?? cellSize * 0.01;

  // Handle degenerate cases
  if (cellSize === 0) {
    return { x: minX, y: minY };
  }

  // Priority queue (simple array sorted by maxDistance)
  const queue: Cell[] = [];

  // Initial cell covering the bounding box
  const halfSize = cellSize / 2;
  for (let x = minX; x < maxX; x += cellSize) {
    for (let y = minY; y < maxY; y += cellSize) {
      queue.push(createCell(x + halfSize, y + halfSize, halfSize, polygon));
    }
  }

  // Take centroid as initial best guess
  const centroid = calculatePolygonCentroid(polygon);
  let bestCell = createCell(centroid.x, centroid.y, 0, polygon);

  // If centroid is inside, use it as starting point
  if (bestCell.distance > 0) {
    // Also try bounding box center
    const bboxCell = createCell(minX + width / 2, minY + height / 2, 0, polygon);
    if (bboxCell.distance > bestCell.distance) {
      bestCell = bboxCell;
    }
  }

  // Process cells
  while (queue.length > 0) {
    // Get cell with highest potential (max distance)
    queue.sort((a, b) => b.maxDistance - a.maxDistance);
    const cell = queue.shift()!;

    // Update best cell if this cell's center is better
    if (cell.distance > bestCell.distance) {
      bestCell = cell;
    }

    // If this cell can't improve the result, skip
    if (cell.maxDistance - bestCell.distance <= actualPrecision) {
      continue;
    }

    // Split cell into 4 children
    const h = cell.halfSize / 2;
    queue.push(createCell(cell.x - h, cell.y - h, h, polygon));
    queue.push(createCell(cell.x + h, cell.y - h, h, polygon));
    queue.push(createCell(cell.x - h, cell.y + h, h, polygon));
    queue.push(createCell(cell.x + h, cell.y + h, h, polygon));
  }

  return { x: bestCell.x, y: bestCell.y };
};

/**
 * Check if two line segments intersect (not just touch at endpoints)
 *
 * Uses the cross product method to determine intersection.
 * Returns true for proper intersections (crossing), false for touching at endpoints.
 *
 * @param p1 - Start of first segment
 * @param p2 - End of first segment
 * @param p3 - Start of second segment
 * @param p4 - End of second segment
 * @returns True if segments properly intersect
 */
export const doSegmentsIntersect = (p1: Point, p2: Point, p3: Point, p4: Point): boolean => {
  // Calculate direction vectors
  const d1x = p2.x - p1.x;
  const d1y = p2.y - p1.y;
  const d2x = p4.x - p3.x;
  const d2y = p4.y - p3.y;

  // Cross product of directions
  const cross = d1x * d2y - d1y * d2x;

  // Parallel lines (including overlapping)
  if (Math.abs(cross) < 1e-10) {
    return false;
  }

  // Calculate parameters for intersection point
  const dx = p3.x - p1.x;
  const dy = p3.y - p1.y;

  const t = (dx * d2y - dy * d2x) / cross;
  const u = (dx * d1y - dy * d1x) / cross;

  // Check if intersection is within both segments (exclusive of endpoints for "proper" intersection)
  // Using small epsilon to allow touching at endpoints
  const epsilon = 1e-10;
  return t > epsilon && t < 1 - epsilon && u > epsilon && u < 1 - epsilon;
};

/**
 * Check if two polygons overlap (share interior area)
 *
 * Returns true if the polygons overlap (one is inside the other or edges cross).
 * Polygons that only touch at edges or vertices are NOT considered overlapping.
 *
 * @param polygon1 - First polygon vertices
 * @param polygon2 - Second polygon vertices
 * @returns True if polygons overlap
 *
 * @example
 * ```ts
 * const overlaps = doPolygonsOverlap(
 *   [{ x: 0, y: 0 }, { x: 0.5, y: 0 }, { x: 0.5, y: 0.5 }, { x: 0, y: 0.5 }],
 *   [{ x: 0.25, y: 0.25 }, { x: 0.75, y: 0.25 }, { x: 0.75, y: 0.75 }, { x: 0.25, y: 0.75 }]
 * );
 * // Result: true (they overlap)
 * ```
 */
export const doPolygonsOverlap = (polygon1: Point[], polygon2: Point[]): boolean => {
  if (polygon1.length < 3 || polygon2.length < 3) {
    return false;
  }

  // Check if any vertex of polygon1 is strictly inside polygon2
  for (const point of polygon1) {
    if (isPointStrictlyInsidePolygon(point, polygon2)) {
      return true;
    }
  }

  // Check if any vertex of polygon2 is strictly inside polygon1
  for (const point of polygon2) {
    if (isPointStrictlyInsidePolygon(point, polygon1)) {
      return true;
    }
  }

  // Check if any edges cross
  for (let i = 0; i < polygon1.length; i++) {
    const p1 = polygon1[i]!;
    const p2 = polygon1[(i + 1) % polygon1.length]!;

    for (let j = 0; j < polygon2.length; j++) {
      const p3 = polygon2[j]!;
      const p4 = polygon2[(j + 1) % polygon2.length]!;

      if (doSegmentsIntersect(p1, p2, p3, p4)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check if a point is strictly inside a polygon (not on the boundary)
 *
 * This is a stricter version of isPointInPolygon that returns false
 * for points exactly on the polygon boundary.
 *
 * @param point - Point to test
 * @param polygon - Polygon vertices
 * @returns True if point is strictly inside (not on boundary)
 */
export const isPointStrictlyInsidePolygon = (point: Point, polygon: Point[]): boolean => {
  if (!isPointInPolygon(point, polygon)) {
    return false;
  }

  // Check if point is on any edge
  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i]!;
    const p2 = polygon[(i + 1) % polygon.length]!;

    if (isPointOnSegment(point, p1, p2)) {
      return false; // Point is on boundary, not strictly inside
    }
  }

  return true;
};

/**
 * Check if a point lies on a line segment
 *
 * @param point - Point to test
 * @param segStart - Start of segment
 * @param segEnd - End of segment
 * @returns True if point is on the segment
 */
export const isPointOnSegment = (point: Point, segStart: Point, segEnd: Point): boolean => {
  const epsilon = 1e-9;

  // Check if point is within bounding box of segment
  const minX = Math.min(segStart.x, segEnd.x) - epsilon;
  const maxX = Math.max(segStart.x, segEnd.x) + epsilon;
  const minY = Math.min(segStart.y, segEnd.y) - epsilon;
  const maxY = Math.max(segStart.y, segEnd.y) + epsilon;

  if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
    return false;
  }

  // Check if cross product is zero (point is collinear with segment)
  const crossProduct =
    (point.y - segStart.y) * (segEnd.x - segStart.x) -
    (point.x - segStart.x) * (segEnd.y - segStart.y);

  return Math.abs(crossProduct) < epsilon;
};

/**
 * Check if a polygon overlaps with any other polygons in a list
 *
 * @param polygon - Polygon to test
 * @param otherRegions - Other regions to check against
 * @param excludeName - Optional name to exclude from check (e.g., the region being edited)
 * @returns Name of the first overlapping region, or null if no overlap
 */
export const findOverlappingRegion = (
  polygon: Point[],
  otherRegions: WallSectionRegion[],
  excludeName?: string
): string | null => {
  for (const region of otherRegions) {
    if (excludeName && region.wallSectionName === excludeName) {
      continue;
    }

    if (doPolygonsOverlap(polygon, region.polygon)) {
      return region.wallSectionName;
    }
  }

  return null;
};

/**
 * Find the nearest point on a line segment to a given point
 *
 * @param point - The point to find the nearest position from
 * @param segStart - Start of the segment
 * @param segEnd - End of the segment
 * @returns The nearest point on the segment
 */
export const nearestPointOnSegment = (point: Point, segStart: Point, segEnd: Point): Point => {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // Segment is a point
    return { x: segStart.x, y: segStart.y };
  }

  // Calculate projection parameter t
  const t = Math.max(
    0,
    Math.min(1, ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSquared)
  );

  return {
    x: segStart.x + t * dx,
    y: segStart.y + t * dy,
  };
};

/**
 * Calculate distance between two points
 */
export const distanceBetweenPoints = (p1: Point, p2: Point): number => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Find the nearest point on a polygon boundary to a given point
 *
 * @param point - The point to find the nearest boundary position from
 * @param polygon - The polygon boundary
 * @returns The nearest point on the polygon boundary
 */
export const nearestPointOnPolygonBoundary = (point: Point, polygon: Point[]): Point => {
  if (polygon.length < 2) {
    return polygon[0] ?? { x: 0, y: 0 };
  }

  let nearestPoint: Point = polygon[0]!;
  let minDistance = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const segStart = polygon[i]!;
    const segEnd = polygon[(i + 1) % polygon.length]!;

    const candidatePoint = nearestPointOnSegment(point, segStart, segEnd);
    const distance = distanceBetweenPoints(point, candidatePoint);

    if (distance < minDistance) {
      minDistance = distance;
      nearestPoint = candidatePoint;
    }
  }

  return nearestPoint;
};

/**
 * Snap a vertex position to avoid polygon overlap
 *
 * When moving a vertex would cause the polygon to overlap with another,
 * this function finds the nearest valid position that doesn't cause overlap.
 *
 * @param proposedPosition - The desired vertex position
 * @param vertexIndex - Index of the vertex being moved
 * @param currentPolygon - The polygon being edited
 * @param otherRegions - Other regions to check against
 * @param excludeName - Name of region being edited (to exclude from checks)
 * @returns The snapped position (same as proposed if no overlap, or adjusted to boundary)
 */
/**
 * Snap a point to nearby region vertices or edges during drawing
 *
 * When drawing a new region, this helps create regions that perfectly
 * align with existing regions by snapping to their vertices or edges.
 *
 * @param point - The point to potentially snap
 * @param regions - Existing regions to snap to
 * @param snapThreshold - Distance threshold for snapping (in normalized coords, default 0.02)
 * @returns The snapped point and whether snapping occurred
 */
export const snapPointToRegions = (
  point: Point,
  regions: WallSectionRegion[],
  snapThreshold: number = 0.02
): { point: Point; snapped: boolean; snapType: 'vertex' | 'edge' | null } => {
  let nearestDistance = snapThreshold;
  let snappedPoint: Point = point;
  let snapType: 'vertex' | 'edge' | null = null;

  for (const region of regions) {
    const polygon = region.polygon;

    // First, check vertices (higher priority than edges)
    for (const vertex of polygon) {
      const distance = distanceBetweenPoints(point, vertex);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        snappedPoint = { x: vertex.x, y: vertex.y };
        snapType = 'vertex';
      }
    }
  }

  // If we found a vertex snap, return it (vertices have priority)
  if (snapType === 'vertex') {
    return { point: snappedPoint, snapped: true, snapType };
  }

  // Check edges
  for (const region of regions) {
    const polygon = region.polygon;

    for (let i = 0; i < polygon.length; i++) {
      const segStart = polygon[i]!;
      const segEnd = polygon[(i + 1) % polygon.length]!;

      const nearestOnEdge = nearestPointOnSegment(point, segStart, segEnd);
      const distance = distanceBetweenPoints(point, nearestOnEdge);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        snappedPoint = nearestOnEdge;
        snapType = 'edge';
      }
    }
  }

  return {
    point: snappedPoint,
    snapped: snapType !== null,
    snapType,
  };
};

export const snapVertexToAvoidOverlap = (
  proposedPosition: Point,
  vertexIndex: number,
  currentPolygon: Point[],
  otherRegions: WallSectionRegion[],
  excludeName?: string
): { position: Point; wasSnapped: boolean } => {
  // Create the polygon with the proposed position
  const testPolygon = currentPolygon.map((point, index) =>
    index === vertexIndex ? proposedPosition : point
  );

  // Check for overlaps
  for (const region of otherRegions) {
    if (excludeName && region.wallSectionName === excludeName) {
      continue;
    }

    if (doPolygonsOverlap(testPolygon, region.polygon)) {
      // Find the nearest point on the conflicting polygon's boundary
      const nearestBoundaryPoint = nearestPointOnPolygonBoundary(proposedPosition, region.polygon);

      // Move slightly away from the boundary to avoid touching issues
      // Calculate direction from boundary point to proposed position
      const dx = proposedPosition.x - nearestBoundaryPoint.x;
      const dy = proposedPosition.y - nearestBoundaryPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance > 0) {
        // Offset slightly in the direction away from the polygon
        const epsilon = 0.001; // Small offset to ensure we're outside
        const snappedPosition: Point = {
          x: nearestBoundaryPoint.x + (dx / distance) * epsilon,
          y: nearestBoundaryPoint.y + (dy / distance) * epsilon,
        };

        // Verify this position doesn't cause overlap
        const verifyPolygon = currentPolygon.map((point, index) =>
          index === vertexIndex ? snappedPosition : point
        );

        if (!doPolygonsOverlap(verifyPolygon, region.polygon)) {
          return { position: snappedPosition, wasSnapped: true };
        }
      }

      // If we couldn't find a good snapped position, use the boundary point
      return { position: nearestBoundaryPoint, wasSnapped: true };
    }
  }

  // No overlap, return original position
  return { position: proposedPosition, wasSnapped: false };
};
